import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
    const [loading, setLoading] = useState(true);
    const [consumer, setConsumer] = useState(null);
    const [needsConsent, setNeedsConsent] = useState(false);

    const refresh = useCallback(async () => {
        try {
            const r = await api.get("/me");
            setConsumer(r.data.consumer);
            setNeedsConsent(r.data.needs_consent);
        } catch (err) {
            // 401 is the expected case for an unauthenticated visitor — treat as "no session".
            // Anything else we surface for debugging.
            if (err?.response?.status !== 401) {
                console.warn("[auth] refresh failed:", err?.message || err);
            }
            setConsumer(null);
            setNeedsConsent(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout");
        } catch (err) {
            console.warn("[auth] logout request failed (clearing local state anyway):", err?.message || err);
        }
        setConsumer(null);
        setNeedsConsent(false);
    }, []);

    // Memoised so context consumers don't re-render on unrelated parent renders.
    const value = useMemo(
        () => ({ loading, consumer, needsConsent, setConsumer, setNeedsConsent, refresh, logout }),
        [loading, consumer, needsConsent, refresh, logout],
    );

    return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
    return useContext(AuthCtx);
}
